import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { logger } from '@/services/logger';

type SyncTripMembershipResponse = {
  success: boolean;
  membersCount?: number;
  memberIdsCount?: number;
};

const buildMemberLists = (tripData: any) => {
  const owner = tripData?.owner;
  const members = Array.isArray(tripData?.members) ? tripData.members : [];
  const memberIds = Array.isArray(tripData?.memberIds) ? tripData.memberIds : [];
  const collaborators = Array.isArray(tripData?.collaborators) ? tripData.collaborators : [];
  const collaboratorIds = collaborators.map((c: any) => c?.userId).filter(Boolean);
  const nextMembers = Array.from(new Set([owner, ...members, ...collaboratorIds].filter(Boolean)));
  const nextMemberIds = Array.from(new Set([owner, ...memberIds, ...collaboratorIds, ...members].filter(Boolean)));
  return { owner, nextMembers, nextMemberIds };
};

async function repairTripMembershipAsOwner(tripId: string, tripData: any, uid: string): Promise<SyncTripMembershipResponse> {
  const tripRef = doc(db, 'trips', tripId);
  const { nextMembers, nextMemberIds } = buildMemberLists(tripData);
  await updateDoc(tripRef, {
    members: nextMembers,
    memberIds: nextMemberIds,
    lastEditedBy: uid,
    lastEditedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { success: true, membersCount: nextMembers.length, memberIdsCount: nextMemberIds.length };
}

async function repairTripMembershipAsCollaborator(tripId: string, tripData: any, uid: string): Promise<SyncTripMembershipResponse> {
  const collaborators = Array.isArray(tripData?.collaborators) ? tripData.collaborators : [];
  const members = Array.isArray(tripData?.members) ? tripData.members : [];
  const memberIds = Array.isArray(tripData?.memberIds) ? tripData.memberIds : [];
  const isCollaborator = collaborators.some((c: any) => c?.userId === uid);
  const alreadyMember = members.includes(uid) || memberIds.includes(uid);
  if (!isCollaborator || alreadyMember) {
    return { success: false };
  }
  const nextMembers = Array.from(new Set([...members, uid].filter(Boolean)));
  const nextMemberIds = Array.from(new Set([...memberIds, uid, ...members].filter(Boolean)));
  const tripRef = doc(db, 'trips', tripId);
  await updateDoc(tripRef, {
    members: nextMembers,
    memberIds: nextMemberIds,
    lastEditedBy: uid,
    lastEditedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { success: true, membersCount: nextMembers.length, memberIdsCount: nextMemberIds.length };
}

async function repairTripMembershipClient(tripId: string): Promise<SyncTripMembershipResponse> {
  const uid = auth.currentUser?.uid;
  if (!uid) return { success: false };
  const tripRef = doc(db, 'trips', tripId);
  
  // First, try to read the trip - this works if user is already in members/memberIds
  let tripSnap: Awaited<ReturnType<typeof getDoc>> | null = null;
  try {
    tripSnap = await getDoc(tripRef);
  } catch (readError: any) {
    // Direct blind self-add is intentionally disabled to prevent unauthorized joins.
    // Membership repairs should happen through the secured callable.
    logger.warn('[TripMembership] Cannot read trip and client fallback is disabled', {
      tripId,
      uid,
      error: readError?.message,
    });
    return { success: false };
  }
  
  if (!tripSnap || !tripSnap.exists()) {
    return { success: false };
  }
  
  const data = tripSnap.data() as { owner?: string } | undefined;
  if (data?.owner === uid) {
    return repairTripMembershipAsOwner(tripId, data, uid);
  }
  return repairTripMembershipAsCollaborator(tripId, data, uid);
}

export async function syncTripMembership(tripId: string): Promise<SyncTripMembershipResponse> {
  // Lazy import to avoid Jest ESM parsing issues with firebase/functions in Node test env.
  try {
    const { httpsCallable, functions } = await import('@/lib/functions');
    const callable = httpsCallable(functions, 'syncTripMembership');
    const res = await callable({ tripId });
    const data = (res?.data ?? { success: true }) as SyncTripMembershipResponse;
    if (!data?.success) {
      return await repairTripMembershipClient(tripId);
    }
    return data;
  } catch (error: any) {
    const code = error?.code || error?.name;
    // If the callable isn't deployed yet, avoid surfacing "not-found" to users.
    if (code === 'not-found') {
      return await repairTripMembershipClient(tripId);
    }
    throw error;
  }
}

export function isPermissionError(err: any): boolean {
  const code = err?.code || err?.name;
  return code === 'permission-denied' || code === 'missing-or-insufficient-permissions';
}

